import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

// ============================================================
// Reusable UI Component Library
// Single source of truth for all visual patterns.
// To restyle the app, modify these components — not App.js.
// ============================================================

// ---- BUTTON ----
// The keyboard focus ring.
//
// Btn, IconBtn and TextLink had NO focus state -- all 576 of them -- so
// keyboard and screen-reader users could not see where they were in the
// app at all. focus-visible rather than focus, so a mouse click does not
// leave a ring behind; ring-offset so the ring reads against a filled
// button as well as a white card.
//
// brand-400 specifically: it is visible both on white and on a brand-600
// fill. The ramp had no 400 step, which is a large part of why this was
// never done -- there was no right colour to reach for.
const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1";

const BTN_BASE = "inline-flex items-center justify-center font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " + FOCUS_RING;
const BTN_VARIANTS = {
  primary:   "bg-brand-600 text-white hover:bg-brand-700",
  secondary: "text-brand-600 border border-brand-200 hover:bg-brand-50 bg-white",
  danger:    "text-danger-600 border border-danger-200 hover:bg-danger-50 bg-white",
  "danger-fill": "bg-danger-600 text-white hover:bg-danger-700",
  success:   "text-success-600 border border-success-200 hover:bg-success-50 bg-white",
  "success-fill": "bg-success-600 text-white hover:bg-success-700",
  // Positive has its own palette (softer green than success). Useful for
  // confirmations and "approve/authorize" affordances where we already
  // use positive- tokens elsewhere in the theme.
  positive:  "text-positive-700 border border-positive-200 hover:bg-positive-100 bg-positive-50",
  "positive-fill": "bg-positive-600 text-white hover:bg-positive-700",
  // Notice = pause/warn-ish. Used for Pause-toggle affordances in Autopay.
  notice:    "text-notice-500 border border-notice-200 hover:bg-notice-50 bg-white",
  // Info (blue) — "Add Charge", informational/neutral-positive actions.
  info:      "text-info-700 border border-info-200 hover:bg-info-100 bg-info-50",
  // Accent (violet) — used for bank-rules engine affordances.
  accent:    "text-accent-700 border border-accent-200 hover:bg-accent-100 bg-accent-50",
  "accent-fill": "bg-accent-600 text-white hover:bg-accent-700",
  // Dark (neutral-800) — used for CSV import wizard Next/Back/Continue chrome.
  dark:      "bg-neutral-800 text-white hover:bg-neutral-700",
  ghost:     "text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100",
  purple:    "text-highlight-600 border border-highlight-200 hover:bg-highlight-50 bg-white",
  amber:     "text-warn-600 border border-warn-200 hover:bg-warn-50 bg-white",
  "warning-fill": "bg-warn-600 text-white hover:bg-warn-700",
  slate:     "text-neutral-600 bg-neutral-100 hover:bg-neutral-200",
};
// Size changes the padding and the type, NOT the shape. This used to
// carry three different radii across four sizes -- rounded-lg, xl and 2xl
// -- so a small button and a large one were differently rounded and two
// buttons side by side in different sizes looked like different
// components. Controls are rounded-lg; surfaces are rounded-xl.
const BTN_SIZES = {
  xs: "text-xs px-2 py-1 gap-1",
  sm: "text-xs px-3 py-1.5 gap-1.5",
  md: "text-sm px-3.5 py-1.5 gap-1.5",
  lg: "text-sm px-5 py-2 gap-2",
};
const BTN_SHAPE = "rounded-lg";

export function Btn({ variant = "primary", size = "md", className = "", icon, type, children, busy, busyLabel, onClick, disabled, ...props }) {
  // Default to type="button". HTML's <button> defaults to type="submit",
  // which — if the button ever ends up inside a <form> — can fire an
  // implicit submit on Enter and trigger the *first* button's onClick
  // instead of the one the user intended. Pin type so our buttons stay
  // inert to form semantics unless a caller explicitly opts in.
  //
  // WORK IN PROGRESS IS SHOWN, NOT HIDDEN.
  //
  // The app has 101 guardSubmit calls, so double-clicking rarely does damage
  // — but guardSubmit is module-level state the UI cannot see, so it drops
  // the second click SILENTLY. The button keeps its full colour and does not
  // move, which reads as "my click missed" and invites another. Saving a
  // reconciliation writes thousands of rows; it sat indigo and inert the
  // whole time, then announced success from nowhere.
  //
  // So: if onClick returns a promise, this button knows it is working. It
  // disables itself, shows a spinner, and re-enables when the promise
  // settles — including when it rejects, because a button stuck disabled
  // after a failure is worse than one that never disabled at all. No call
  // site has to opt in; an async handler is the opt-in.
  const [selfBusy, setSelfBusy] = React.useState(false);
  const alive = React.useRef(true);
  // Set true in the BODY, not just false in the cleanup.
  //
  // React 18 StrictMode double-invokes effects in development: mount,
  // cleanup, mount. With only the cleanup writing this ref, the first cycle
  // left it false forever and setSelfBusy(false) never ran again -- so the
  // first async click disabled the button permanently. The property wizard's
  // Next stopped working after one press, which is how this was found: by
  // driving the real wizard in a browser, not by reading the code.
  //
  // Production never double-invokes, so the live site was unaffected. That is
  // luck, not design: any remount would have done the same thing.
  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const handle = onClick && ((e) => {
    const r = onClick(e);
    // Only track a thenable. A sync handler must STAY sync -- wrapping every
    // click in async would defer it by a microtask and change ordering across
    // the app for no benefit.
    if (!r || typeof r.then !== "function") return r;
    setSelfBusy(true);
    // Re-enable on rejection as well as success. A button left disabled after
    // a failure is worse than one that never disabled: the error is dismissed
    // and the control is dead with nothing explaining why.
    //
    // The catch also stops a rejecting handler becoming an unhandled rejection
    // it would not have been before this wrapper existed -- it is re-thrown on
    // its own so window.onunhandledrejection still sees it and Sentry still
    // records it, but the click itself resolves cleanly.
    r.then(
      () => { if (alive.current) setSelfBusy(false); },
      (err) => { if (alive.current) setSelfBusy(false); setTimeout(() => { throw err; }); }
    );
    return r;
  });

  const isBusy = busy || selfBusy;
  return (
    <button type={type || "button"} onClick={handle} disabled={disabled || isBusy}
      aria-busy={isBusy || undefined}
      className={`${BTN_BASE} ${BTN_SHAPE} ${BTN_VARIANTS[variant] || BTN_VARIANTS.primary} ${BTN_SIZES[size] || BTN_SIZES.md} ${className}`} {...props}>
      {isBusy
        ? <span className="inline-block w-3.5 h-3.5 mr-1.5 rounded-full border-2 border-current border-r-transparent animate-spin" aria-hidden="true" />
        : icon && <span className="material-icons-outlined text-sm">{icon}</span>}
      {isBusy && busyLabel ? busyLabel : children}
    </button>
  );
}

// ---- ICON BUTTON ----
export function IconBtn({ icon, className = "", title, onClick, disabled, ...props }) {
  // Same rule as Btn: an async handler makes the button show it is working.
  // An icon button is the likeliest to be clicked twice, because there is no
  // label to re-read and nothing else on it that could change.
  const [busy, setBusy] = React.useState(false);
  const alive = React.useRef(true);
  // Same as Btn above: the ref must be set true in the body. StrictMode's
  // mount/cleanup/mount left it false and the icon spun forever.
  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const handle = onClick && ((e) => {
    const r = onClick(e);
    if (!r || typeof r.then !== "function") return r;
    setBusy(true);
    r.then(
      () => { if (alive.current) setBusy(false); },
      (err) => { if (alive.current) setBusy(false); setTimeout(() => { throw err; }); }
    );
    return r;
  });
  return (
    <button onClick={handle} disabled={disabled || busy} aria-busy={busy || undefined}
      className={`w-8 h-8 flex items-center justify-center rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING} ${className}`} title={title} {...props}>
      {busy
        ? <span className="inline-block w-4 h-4 rounded-full border-2 border-current border-r-transparent animate-spin" aria-hidden="true" />
        : <span className="material-icons-outlined text-lg">{icon}</span>}
    </button>
  );
}

// ---- CARD ----
// ---- SKELETON ----
// A placeholder shaped like the content that is coming.
//
// Every loading state in the app was either a centred spinner or the
// single word "Loading…". For a table that is the worst option available:
// the row area collapses to one line, so the page jumps when the data
// lands, and a spinner in the middle of a table says nothing about what
// is arriving. DataTable's loading state draws skeleton rows instead --
// one per column, at the table's real row height -- so the layout is
// already the right shape before the first figure appears.
export function Skeleton({ className = "" }) {
  return <span className={`inline-block bg-neutral-200 rounded animate-pulse align-middle ${className}`} />;
}

// ---- ALERT ----
// A tinted banner: a warning above a form, a confirmation, a blocked
// action's reason.
//
// There were 73 distinct recipes for a tinted block across the app. The
// alert-shaped ones -- tint, matching border, padding -- had drifted to
// rounded-lg / rounded-xl / rounded-3xl and p-3 / p-4, so two warnings on
// adjacent pages did not look like the same kind of thing.
//
// ALERT_TONE is exported because normalising the existing 40-odd
// hand-written banners means rewriting their className, not their
// contents -- restructuring JSX is how a migration loses the children it
// was supposed to keep.
export const ALERT_TONE = {
  danger:   "bg-danger-50 border-danger-200 text-danger-800",
  warn:     "bg-warn-50 border-warn-200 text-warn-800",
  success:  "bg-success-50 border-success-200 text-success-800",
  info:     "bg-info-50 border-info-200 text-info-800",
  positive: "bg-positive-50 border-positive-200 text-positive-800",
  notice:   "bg-notice-50 border-notice-200 text-notice-800",
};
export const ALERT_SHAPE = "border rounded-xl p-3";

export function Alert({ tone = "info", icon, title, children, className = "" }) {
  return (
    <div role={tone === "danger" ? "alert" : undefined}
         className={`${ALERT_SHAPE} ${ALERT_TONE[tone] || ALERT_TONE.info} ${className}`}>
      <div className="flex items-start gap-2">
        {icon && <span className="material-icons-outlined text-base shrink-0 mt-0.5">{icon}</span>}
        <div className="min-w-0 flex-1 text-sm">
          {title && <p className="font-semibold mb-0.5">{title}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

// ---- SURFACES ----
// One recipe per KIND of surface, and nothing spells its own.
//
// The app had fourteen different ways to draw what is conceptually the
// same thing -- a panel with content in it. The top few, by count:
//
//     47x  rounded-xl  border border-neutral-200
//     47x  rounded-3xl shadow-card
//     39x  rounded-3xl border border-brand-50
//     21x  rounded-xl  shadow-card
//     21x  rounded-xl  border border-brand-100
//     12x  rounded-xl  border border-neutral-100
//
// Three radii (12px / 16px / 24px) and three border colours, chosen ad
// hoc, for the same object. The Card component existed and eleven places
// used it.
//
// RADIUS: standardised on rounded-xl (12px). Two reasons, and it is a
// visible decision so it is stated rather than buried -- rounded-xl is
// already the most common radius in the codebase (111 uses across its
// variants against 86 for rounded-3xl), and 24px reads as a consumer app
// rather than as accounting software. QuickBooks sits nearer 8-12px.
// Because it is a token now, changing this back is one edit here.
export const SURFACE = {
  // The default: a panel on the page background.
  card:   "bg-white rounded-xl border border-neutral-200",
  // A card that should lift off the page -- dashboard tiles, summaries.
  raised: "bg-white rounded-xl border border-neutral-200 shadow-card",
  // A recessed area INSIDE a card: a filter strip, a nested summary.
  inset:  "bg-neutral-50 rounded-xl border border-neutral-100",
  // Floating above everything: menus, popovers, dialogs.
  overlay: "bg-white rounded-xl border border-neutral-200 shadow-pop",
};

// Card padding, named.
//
// The shared card recipe carried p-4 in 87 places, p-6 in 23, p-5 in 18,
// p-8 in 9, p-3 in 5 and p-10 once -- six densities for the same object,
// and Card's own default was p-5, the THIRD most common. Naming three
// levels gives new code something to reach for other than a number, and
// makes the default the one the app actually uses.
//
// A raw class still works ({padding="p-8"}) for the handful of places
// that genuinely want something else, so this constrains without
// blocking.
export const PAD = {
  tight: "p-3",   // dense lists, nested panels
  card:  "p-4",   // the default
  roomy: "p-6",   // modals, wide single-column panels
};

export function Card({ className = "", padding = "card", variant = "card", children, ...props }) {
  padding = PAD[padding] || padding;
  return (
    <div className={`${SURFACE[variant] || SURFACE.card} ${padding} ${className}`} {...props}>
      {children}
    </div>
  );
}

// ---- INPUT / SELECT / TEXTAREA ----
// Size tokens — keep "md" as the default so existing screens don't shift.
// Use size="sm" for dense admin/settings forms where vertical space matters.
// Same rule as buttons: size is padding and type, not shape. An input
// next to a button must share its corner or the pair looks mismatched.
const INPUT_SIZES = {
  sm: "px-2.5 py-1 text-xs min-h-8",
  md: "px-3 py-1.5 text-sm min-h-9",
};
const INPUT_SHAPE = "rounded-lg";
const INPUT_COMMON = "border border-brand-100 focus:border-brand-300 focus:outline-none transition-colors";
function inputBase(size, hasExplicitWidth) {
  return `${INPUT_COMMON} ${INPUT_SHAPE} ${INPUT_SIZES[size] || INPUT_SIZES.md}${hasExplicitWidth ? "" : " w-full"}`;
}

export function Input({ className = "", size = "md", ...props }) {
  // Auto-apply sensible defaults by type
  const defaults = {};
  if (props.type === "date") { defaults.min = props.min || "2000-01-01"; defaults.max = props.max || "2099-12-31"; }
  else if (props.type === "email") { defaults.maxLength = props.maxLength || 254; }
  else if (props.type === "tel") { defaults.maxLength = props.maxLength || 14; }
  else if (props.type === "number") { defaults.step = props.step || "any"; }
  else if (props.type === "text" && !props.maxLength) { defaults.maxLength = 200; }
  const base = inputBase(size, /\bw-/.test(className));
  return <input className={`${base} ${className}`} {...defaults} {...props} />;
}

// Currency field shown in accounting format (thousands separators + 2 decimals)
// when idle, and as the plain editable number while focused — so formatting
// never fights the caret mid-type. Stores/returns the RAW numeric string
// (no commas); onChange receives that string, not an event. Use for money
// amounts; NOT for rates/percentages/counts.
export function MoneyInput({ className = "", size = "md", value, onChange, ...props }) {
  const [focused, setFocused] = useState(false);
  const raw = (value === null || value === undefined) ? "" : String(value);
  const stripped = raw.replace(/,/g, "");
  const display = (focused || stripped === "" || isNaN(Number(stripped)))
    ? raw
    : Number(stripped).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <Input
      {...props}
      type="text"
      inputMode="decimal"
      size={size}
      className={className}
      value={display}
      onFocus={(e) => { setFocused(true); props.onFocus && props.onFocus(e); }}
      onBlur={(e) => { setFocused(false); props.onBlur && props.onBlur(e); }}
      onChange={(e) => {
        const v = e.target.value.replace(/,/g, "");
        if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(v);
      }}
    />
  );
}

// SELECT_HEIGHT, not padding. A <select> on mobile is drawn by the
// platform, and mobile Chrome does not honour vertical padding on one the
// way it does on an <input> -- the filter selects measured ~22px tall on
// an iPhone while the text input directly above them sat at 32px, which
// the screenshot makes obvious. An explicit height is deterministic on
// both. min-height was not enough either; it is the padding that fails to
// apply, so the box never grows to meet it.
const SELECT_HEIGHT = { sm: "h-8", md: "h-9" };

export function Select({ className = "", filter, size = "md", children, ...props }) {
  const base = `${inputBase(size, filter || /\bw-/.test(className))} ${SELECT_HEIGHT[size] || SELECT_HEIGHT.md}`;
  const widthCls = filter ? " w-auto" : "";
  // Fall back to the first option's text as the accessible name.
  //
  // axe reported critical select-name failures on ten routes: filter
  // dropdowns are rendered bare, with their meaning carried only by the
  // selected option, so a screen reader announces "combo box" and
  // nothing else. The first option is almost always the "All …" label
  // that names the filter ("All Status", "All Types", "All Cities"),
  // which is exactly the name a user needs. An explicit aria-label
  // always wins.
  let derived;
  if (!props["aria-label"] && !props["aria-labelledby"]) {
    const first = React.Children.toArray(children).find(c => c && c.type === "option");
    const txt = first && typeof first.props?.children === "string" ? first.props.children.trim() : "";
    if (txt) derived = txt;
  }
  return (
    <select className={`${base}${widthCls} ${className}`} aria-label={derived} {...props}>
      {children}
    </select>
  );
}

export function Textarea({ className = "", rows = 3, size = "md", ...props }) {
  const base = inputBase(size, /\bw-/.test(className));
  return <textarea className={`${base} ${className}`} rows={rows} maxLength={props.maxLength || 5000} {...props} />;
}

// ---- FORM FIELD (label + input wrapper) ----
export function FormField({ label, required, className = "", size = "md", children }) {
  const labelCls = size === "sm"
    ? "text-2xs font-medium text-neutral-500 uppercase tracking-wider block mb-1"
    : "text-xs font-medium text-neutral-500 uppercase tracking-widest block mb-1";
  return (
    <div className={className}>
      {label && (
        <label className={labelCls}>
          {label} {required && "*"}
        </label>
      )}
      {children}
    </div>
  );
}

// ---- BADGE ----
const BADGE_COLORS = {
  green:  "bg-success-50 text-success-700 border-success-200",
  red:    "bg-danger-50 text-danger-700 border-danger-200",
  yellow: "bg-warn-50 text-warn-700 border-warn-200",
  blue:   "bg-info-50 text-info-700 border-info-200",
  purple: "bg-highlight-50 text-highlight-700 border-highlight-200",
  gray:   "bg-neutral-50 text-neutral-600 border-neutral-200",
  indigo: "bg-brand-50 text-brand-700 border-brand-200",
};
const STATUS_MAP = {
  active: "green", occupied: "green", paid: "green", completed: "green", sent: "green", posted: "green",
  pending: "yellow", in_progress: "yellow", open: "yellow", draft: "yellow", unpaid: "yellow", notice: "yellow",
  inactive: "gray", vacant: "gray", archived: "gray", cancelled: "gray",
  overdue: "red", urgent: "red", emergency: "red", rejected: "red", failed: "red",
  high: "red", normal: "blue", low: "gray",
};

export function Badge({ status, label, color, className = "" }) {
  const resolvedColor = color || STATUS_MAP[status] || STATUS_MAP[label?.toLowerCase()] || "gray";
  const displayLabel = label || (status ? status.replace(/_/g, " ") : "");
  return (
    <span className={`inline-flex items-center text-xs font-bold px-2.5 py-0.5 rounded-full border ${BADGE_COLORS[resolvedColor] || BADGE_COLORS.gray} ${className}`}>
      {displayLabel}
    </span>
  );
}

// ---- PAGE HEADER ----
// `size="section"` is the same header one step down, for a sub-page inside
// a section -- Chart of Accounts, Journal Entries, Reports and Bank
// Transactions all sit under Accounting rather than being top-level pages.
// Six of those had hand-written their own title/subtitle/actions row at
// text-lg, which is why they drifted from each other and from this.
const HEADER_SIZE = {
  page:    { wrap: "mb-5", title: "text-xl md:text-2xl", sub: "text-xs" },
  section: { wrap: "mb-4", title: "text-lg",             sub: "text-sm" },
};
export function PageHeader({ title, subtitle, children, size = "page" }) {
  const z = HEADER_SIZE[size] || HEADER_SIZE.page;
  return (
    <div className={`flex flex-col md:flex-row md:items-center justify-between gap-2 ${z.wrap}`}>
      <div>
        <h2 className={`${z.title} font-display font-bold text-neutral-800`}>{title}</h2>
        {subtitle && <p className={`${z.sub} text-neutral-400 mt-0.5`}>{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

// ---- SECTION TITLE (within a page) ----
export function SectionTitle({ children, className = "" }) {
  return <h3 className={`font-display font-bold text-neutral-700 text-sm mb-3 uppercase tracking-wide ${className}`}>{children}</h3>;
}

// ---- EMPTY STATE ----
// "Nothing here yet."
//
// This component existed and three places used it. Seventy-one others
// hand-wrote a centred grey line, with the padding picked per site --
// py-4, py-6, py-8, py-12 -- and sometimes text-sm, sometimes not. Bare
// grey text is what makes an app feel unfinished.
//
// The sizes exist because one size is genuinely wrong: a 16rem-tall empty
// state with a 36px icon inside a small dashboard card is worse than the
// grey line it replaced. So `inline` is for a panel inside a card and
// drops the icon entirely, `compact` for a section, `page` for a whole
// empty page. Migrating the 71 mapped their old padding onto these:
// py-4/py-6 -> inline, py-8 -> compact, py-12 and up -> page.
const EMPTY_SIZE = {
  inline:  { wrap: "py-4",  icon: null,       title: "text-sm" },
  compact: { wrap: "py-8",  icon: "text-2xl", title: "text-sm font-medium" },
  page:    { wrap: "py-16", icon: "text-4xl", title: "text-sm font-medium" },
};
export function EmptyState({ icon = "inbox", title, subtitle, size = "page", className = "" }) {
  const z = EMPTY_SIZE[size] || EMPTY_SIZE.page;
  return (
    <div className={`text-center text-neutral-400 ${z.wrap} ${className}`}>
      {z.icon && icon && <span className={`material-icons-outlined mb-2 ${z.icon}`}>{icon}</span>}
      {title && <p className={z.title}>{title}</p>}
      {subtitle && <p className="text-xs mt-1">{subtitle}</p>}
    </div>
  );
}

// ---- TAB BAR ----
// Tabs.
//
// This component existed and NOTHING used it, because it rendered filled
// pills while every tab bar in the app is an UNDERLINE bar -- which is
// also what QuickBooks uses. So eleven pages each hand-wrote the same
// `border-b-2` button and drifted: px-3 vs px-4 vs px-5, py-1.5 vs py-2 vs
// py-3, text-xs vs text-sm, and only some of them with a hover state.
//
// `tabs` takes either [id, label] pairs or { id, label, icon, count }
// objects, because several bars show an icon and several append a count
// ("Expiring (3)") -- both of which had been spelled into the label by
// hand at the call site.
const TAB_SIZE = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-4 py-3 text-sm",
};
export function TabBar({ tabs, active, onChange, size = "md", variant = "underline", className = "" }) {
  const pad = TAB_SIZE[size] || TAB_SIZE.md;
  const items = tabs.map(t => (Array.isArray(t) ? { id: t[0], label: t[1] } : t));
  // no-scrollbar: the strip still scrolls when it needs to, but macOS stops
  // painting a track under two short tabs, which read as a grey loading bar
  // sitting beneath them.
  const wrap = variant === "pill"
    ? "flex gap-1 overflow-x-auto no-scrollbar"
    : "flex gap-1 overflow-x-auto no-scrollbar border-b border-neutral-200";
  // A strip wider than the screen scrolls, but nothing said so: on a phone
  // the last tab was simply cut off ("Terminated/All", "Error Log",
  // "History (200)"). A fade and a chevron now show while more is hidden
  // to the right.
  const stripRef = useRef(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = stripRef.current; if (!el) return undefined;
    const check = () => setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    check();
    el.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (ro) ro.observe(el);
    return () => { el.removeEventListener("scroll", check); window.removeEventListener("resize", check); if (ro) ro.disconnect(); };
  }, [items.length]);
  return (
    <div className={`relative min-w-0 ${className}`}>
    <div ref={stripRef} className={wrap} role="tablist">
      {items.map(t => {
        const on = active === t.id;
        const look = variant === "pill"
          ? `rounded-lg ${on ? "bg-brand-600 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`
          // -mb-px pulls the active underline onto the container's border
          // so the two read as one line rather than stacking into two.
          : `border-b-2 -mb-px ${on ? "border-brand-600 text-brand-700" : "border-transparent text-neutral-400 hover:text-neutral-600"}`;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on}
            onClick={() => onChange(t.id)}
            // shrink-0: without it a flex child compresses BELOW its content
            // width, so whitespace-nowrap text overflows the button and
            // overlaps the next tab. On a phone that read as
            // "For Review (296)cognized (254)ategorized" -- the labels crammed
            // into each other instead of the strip scrolling. shrink-0 keeps
            // each tab its natural width and lets overflow-x-auto scroll.
            className={`${pad} font-medium whitespace-nowrap shrink-0 transition-colors inline-flex items-center gap-1.5 ${look} ${FOCUS_RING}`}>
            {t.icon && <span className="material-icons-outlined text-base">{t.icon}</span>}
            {t.label}
            {t.count != null && <span className="text-2xs text-neutral-400">({t.count})</span>}
          </button>
        );
      })}
    </div>
    {more && (
      <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-r from-transparent to-white flex items-center justify-end pr-0.5">
        <span className="material-icons-outlined text-neutral-400 text-base">chevron_right</span>
      </div>
    )}
    </div>
  );
}

// ---- STAT CARD (dashboard) ----
export function StatCard({ label, value, icon, color = "indigo", trend }) {
  const bgMap = { indigo: "bg-brand-50", green: "bg-success-50", red: "bg-danger-50", amber: "bg-warn-50", purple: "bg-highlight-50" };
  const textMap = { indigo: "text-brand-600", green: "text-success-600", red: "text-danger-600", amber: "text-warn-600", purple: "text-highlight-600" };
  return (
    <div className={`${SURFACE.raised} ${PAD.card}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${bgMap[color]} rounded-lg flex items-center justify-center`}>
          <span className={`material-icons-outlined ${textMap[color]}`}>{icon}</span>
        </div>
        <div>
          <div className="text-xs text-neutral-400 font-medium">{label}</div>
          <div className="text-xl font-display font-bold text-neutral-800">{value}</div>
          {trend && <div className="text-xs text-success-600 font-medium">{trend}</div>}
        </div>
      </div>
    </div>
  );
}

// ---- BULK ACTION BAR ----
export function BulkBar({ count, label = "item", children, onDeselect }) {
  return (
    <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 mb-4 flex items-center justify-between">
      <span className="text-sm font-medium text-brand-800">{count} {label}{count > 1 ? "s" : ""} selected</span>
      <div className="flex gap-2">
        {children}
        <button onClick={onDeselect} className="text-xs text-neutral-500 px-3 py-1.5 rounded-lg hover:bg-neutral-100">Deselect</button>
      </div>
    </div>
  );
}

// ---- ACCOUNT PICKER (typeahead) ----
export function AccountPicker({ value, onChange, accounts = [], accountTypes = [], showNewOption, placeholder = "Search accounts...", className = "" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Resolve display text for current value
  const selected = accounts.find(a => a.id === value);
  const displayText = selected ? `${selected.code || "•"} ${selected.name}` : "";

  // Filter accounts by search
  const q = search.toLowerCase();
  const filtered = q
    ? accounts.filter(a => a.is_active !== false && (
        (a.name || "").toLowerCase().includes(q) ||
        (a.code || "").toLowerCase().includes(q) ||
        (a.type || "").toLowerCase().includes(q)
      ))
    : accounts.filter(a => a.is_active !== false);

  // Group filtered accounts by type
  const types = accountTypes.length ? accountTypes : [...new Set(filtered.map(a => a.type))];
  const grouped = types.map(type => ({
    type,
    items: filtered.filter(a => a.type === type),
  })).filter(g => g.items.length > 0);

  // Flat list for keyboard nav
  const flatItems = [];
  if (showNewOption) flatItems.push({ id: "__new__", label: "+ New Account", type: "__special__" });
  grouped.forEach(g => g.items.forEach(a => flatItems.push(a)));

  // Close on outside click
  useEffect(() => {
    function handleClick(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlighted >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${highlighted}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);

  const select = useCallback((id) => {
    onChange(id);
    setOpen(false);
    setSearch("");
    setHighlighted(-1);
  }, [onChange]);

  function handleKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlighted >= 0) { e.preventDefault(); select(flatItems[highlighted].id); }
    else if (e.key === "Escape") { setOpen(false); setSearch(""); }
    else if (e.key === "Tab") { setOpen(false); setSearch(""); }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? search : displayText}
        placeholder={value ? displayText : placeholder}
        onChange={e => { setSearch(e.target.value); setHighlighted(-1); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(""); }}
        onKeyDown={handleKeyDown}
        // An <input> clips its value rather than ellipsising it, so a
        // narrow column shows "1100-012 A" with no sign that more exists.
        // The column is wide enough now, but the title keeps the whole
        // account readable on hover at any width the user drags it to.
        title={displayText || undefined}
        className={`${inputBase("md", false)} ${className} pr-7 text-xs`}
        autoComplete="off"
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-300 pointer-events-none text-xs">▾</span>
      {value && !open && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); setSearch(""); inputRef.current?.focus(); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-500 text-xs"
          tabIndex={-1}>✕</button>
      )}
      {open && (
        <div ref={listRef} className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-brand-100 rounded-xl shadow-pop max-h-56 overflow-y-auto">
          {showNewOption && (
            <button type="button" data-idx={0}
              onMouseDown={(e) => { e.preventDefault(); select("__new__"); }}
              className={`w-full text-left px-3 py-1.5 text-xs font-semibold text-brand-600 hover:bg-brand-50 ${highlighted === 0 ? "bg-brand-50" : ""}`}>
              + New Account
            </button>
          )}
          {grouped.length === 0 && <div className="px-3 py-3 text-xs text-neutral-400 text-center">No accounts match "{search}"</div>}
          {grouped.map(g => (
            <div key={g.type}>
              <div className="px-3 py-1 text-2xs font-bold text-neutral-400 uppercase tracking-wider bg-neutral-50 sticky top-0">{g.type}</div>
              {g.items.map(a => {
                const idx = flatItems.indexOf(a);
                return (
                  <button type="button" key={a.id} data-idx={idx}
                    onMouseDown={(e) => { e.preventDefault(); select(a.id); }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-brand-50 flex items-center gap-1 ${highlighted === idx ? "bg-brand-50 text-brand-700" : "text-neutral-700"} ${a.id === value ? "font-semibold" : ""}`}>
                    <span className="text-neutral-400 w-10 shrink-0 tnum">{a.code || "•"}</span>
                    <span className="truncate">{a.name}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- ENTITY PICKER (tenant or vendor, typeahead) ----
//
// This replaces a plain <select> holding two <optgroup>s. It reported
// "can't see any vendors on the bank page" against a company with 84 of
// them -- because 95 tenants came first, and a native dropdown has no
// search. The vendors were ninety-five rows below the fold, so the only
// honest conclusion from the screen was that there were none.
//
// Modelled on AccountPicker above rather than written fresh: same
// typeahead, same keyboard nav, same outside-click close. Two differences
// that matter here -- each row carries its own kind, so a vendor and a
// tenant of the same name are distinguishable, and a name that matches
// nothing offers to create the vendor then and there. There was no way to
// add one from this page at all.
//
// `value` is the encoding the call sites already use: "" or
// "customer:<id>" / "vendor:<id>".
export function EntityPicker({
  value, onChange, tenants = [], vendors = [], onCreateVendor,
  placeholder = "Search tenants and vendors...", className = "",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlighted, setHighlighted] = useState(-1);
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const rows = useMemo(() => [
    ...tenants.map(t => ({ key: `customer:${t.id}`, name: t.name || "", kind: "Tenant", sub: t.property || "" })),
    ...vendors.map(v => ({ key: `vendor:${v.id}`, name: v.name || "", kind: "Vendor", sub: "" })),
  ], [tenants, vendors]);

  const selected = rows.find(r => r.key === value);
  const displayText = selected ? selected.name : "";

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(r => String(r.name || "").toLowerCase().includes(q)) : rows;
  // An exact name already on file must not be offered for creation -- that
  // is how you end up with two vendors called the same thing.
  const exact = q && rows.some(r => r.name.trim().toLowerCase() === q);
  const canCreate = !!onCreateVendor && !!q && !exact;

  const flat = canCreate ? [{ key: "__new__" }, ...filtered] : filtered;

  useEffect(() => {
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (highlighted >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${highlighted}"]`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [highlighted]);

  const pick = useCallback((row) => {
    if (!row) return;
    const [type, id] = row.key.split(":");
    onChange(row.key, { type, id, name: row.name });
    setOpen(false); setSearch(""); setHighlighted(-1);
  }, [onChange]);

  async function createVendor() {
    const name = search.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await onCreateVendor(name);
      // Only select it if it actually came back. A failed insert that still
      // selected the name would leave a reference to a vendor that does not
      // exist, and the save would fail somewhere further along.
      if (created && created.id) {
        onChange(`vendor:${created.id}`, { type: "vendor", id: created.id, name: created.name || name });
        setOpen(false); setSearch(""); setHighlighted(-1);
      }
    } finally { setCreating(false); }
  }

  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(h => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      const row = flat[highlighted];
      if (row.key === "__new__") createVendor(); else pick(row);
    } else if (e.key === "Escape") { setOpen(false); setSearch(""); }
    else if (e.key === "Tab") { setOpen(false); setSearch(""); }
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? search : displayText}
        placeholder={value ? displayText : placeholder}
        onChange={e => { setSearch(e.target.value); setHighlighted(-1); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(""); }}
        onKeyDown={onKeyDown}
        title={displayText || undefined}
        className={`${inputBase("md", false)} pr-7 text-xs`}
        autoComplete="off"
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-300 pointer-events-none text-xs">▾</span>
      {value && !open && (
        <button type="button" tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); onChange("", { type: "", id: "", name: "" }); setSearch(""); inputRef.current?.focus(); }}
          className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-500 text-xs">✕</button>
      )}
      {open && (
        <div ref={listRef} className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-brand-100 rounded-xl shadow-pop max-h-56 overflow-y-auto">
          {canCreate && (
            <button type="button" data-idx={0} disabled={creating}
              onMouseDown={(e) => { e.preventDefault(); createVendor(); }}
              className={`w-full text-left px-3 py-1.5 text-xs font-semibold text-brand-600 hover:bg-brand-50 disabled:opacity-50 ${highlighted === 0 ? "bg-brand-50" : ""}`}>
              {creating ? "Adding…" : `+ Add "${search.trim()}" as a vendor`}
            </button>
          )}
          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-3 text-xs text-neutral-400 text-center">
              {q ? `Nothing matches "${search}"` : "No tenants or vendors yet"}
            </div>
          )}
          {filtered.map(r => {
            const idx = flat.indexOf(r);
            return (
              <button type="button" key={r.key} data-idx={idx}
                onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-brand-50 flex items-center gap-2 ${highlighted === idx ? "bg-brand-50 text-brand-700" : "text-neutral-700"} ${r.key === value ? "font-semibold" : ""}`}>
                <span className={`shrink-0 text-2xs px-1.5 py-0.5 rounded-full ${r.kind === "Vendor" ? "bg-notice-100 text-notice-700" : "bg-brand-100 text-brand-700"}`}>{r.kind}</span>
                <span className="truncate">{r.name}</span>
                {r.sub && <span className="ml-auto text-2xs text-neutral-400 truncate max-w-[45%]">{r.sub}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- FILTER PILL ----
// Accent palette follows the module's visual identity: brand (default),
// positive (Accounting module's green theme), dark (neutral-800 active).
export function FilterPill({ active, onClick, tone = "brand", children, className = "" }) {
  const activeTones = {
    brand:    "bg-brand-600 text-white",
    positive: "bg-positive-600 text-white border-positive-600",
    dark:     "bg-neutral-800 text-white border-neutral-800",
    notice:   "bg-notice-500 text-white",
    "positive-fill": "bg-positive-500 text-white",
    "danger-fill":   "bg-danger-500 text-white",
  };
  const inactiveTones = {
    brand:    "bg-neutral-100 text-neutral-600 hover:bg-neutral-200",
    positive: "bg-white text-neutral-500 border border-neutral-200 hover:border-positive-300",
    dark:     "bg-white text-neutral-400 border border-brand-100",
    notice:   "bg-white text-notice-700 border border-notice-200",
    "positive-fill": "bg-neutral-200 text-neutral-500 hover:bg-neutral-300",
    "danger-fill":   "bg-neutral-200 text-neutral-500 hover:bg-neutral-300",
  };
  const cls = active ? activeTones[tone] || activeTones.brand : inactiveTones[tone] || inactiveTones.brand;
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${cls} ${className}`}>
      {children}
    </button>
  );
}

// ---- CHECKBOX ----
// Pairs an <input type=checkbox> with an optional label. When `label` is
// passed, the entire row is clickable (label wraps input). If the caller
// needs bare-input semantics (e.g., inside a custom grid cell), pass
// `label={null}` and render its own <label>.
// The box renders at the browser default, roughly 13x13 CSS pixels, which
// is far below a usable touch target -- measured 18 of them at 13x13 on
// the Maintenance page at iPhone width. h-4 w-4 makes the box itself
// legible; the padding around it inside the label is what actually gets
// tapped, so an unlabelled checkbox in a table cell still needs the
// caller to give it room.
//
// Also gains the shared focus ring: it had focus:ring (always on, even
// for a mouse click) rather than focus-visible.
export function Checkbox({ label, className = "", ...props }) {
  const input = (
    <input type="checkbox"
      className={`h-4 w-4 shrink-0 rounded border-brand-200 text-brand-600 cursor-pointer ${FOCUS_RING}`}
      {...props} />
  );
  // An unlabelled checkbox -- a row selector in a table -- has only the
  // 16px box as its tap target, which measured 18 such targets under 32px
  // on Maintenance at iPhone width. Clicking a <label> toggles the input
  // it wraps, so padding the label grows the TARGET without growing the
  // box. The negative margin keeps the extra area from shifting the
  // layout around it.
  if (label == null) {
    return (
      <label className={`inline-flex items-center justify-center p-2 -m-2 cursor-pointer ${className}`}>
        {input}
      </label>
    );
  }
  return (
    <label className={`inline-flex items-center gap-2 py-1.5 text-sm text-neutral-700 cursor-pointer ${className}`}>
      {input}
      <span>{label}</span>
    </label>
  );
}

// ---- RADIO ----
export function Radio({ label, className = "", ...props }) {
  const input = (
    <input type="radio" className="border-brand-200 text-brand-600 focus:ring-brand-300" {...props} />
  );
  if (label == null) return input;
  return (
    <label className={`inline-flex items-center gap-2 text-sm text-neutral-700 ${className}`}>
      {input}
      <span>{label}</span>
    </label>
  );
}

// ---- FILE INPUT ----
// Styles the native file picker button. For hidden-input + "Upload" button
// patterns, pass a ref and className="hidden" like any other input.
export function FileInput({ className = "", accept, ...props }) {
  return (
    <input
      type="file"
      accept={accept}
      className={`text-xs file:mr-2 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-brand-700 hover:file:bg-brand-100 ${className}`}
      {...props}
    />
  );
}

// ---- SWITCH ----
// An on/off toggle.
//
// Three of these were hand-written -- dark mode in Admin, a notification
// rule in AdminNotificationRules, a notification setting in Notifications
// -- and all three drifted: w-10 h-5 vs w-11 h-6, left-5 vs translate-x-5
// for the knob, and three different "on" colours (brand-600, positive-500,
// success-500). Only one of the three had an aria-label, and none was
// reachable as a real control: a <button> with no role and no aria-checked
// reads to a screen reader as an unlabelled button, not a switch.
// A quieter switch (Sahil, 2026-10-13: the fat green pill "is ugly throughout
// the app"): a small track in the brand colour when on, a bordered grey when
// off, a knob with a shadow. One look everywhere; `tone` is kept for callers
// but every tone is the brand colour now.
const SWITCH_SIZE = {
  sm: { track: "w-[30px] h-[18px]", knob: "w-3 h-3", on: "left-[15px]", off: "left-[2px]" },
  md: { track: "w-[34px] h-5", knob: "w-3.5 h-3.5", on: "left-[17px]", off: "left-[2px]" },
};
const SWITCH_TONE = {
  brand:    "bg-brand-600 border-brand-600",
  success:  "bg-brand-600 border-brand-600",
  positive: "bg-brand-600 border-brand-600",
};
export function Switch({ checked, onChange, label, size = "sm", tone = "brand", disabled, className = "" }) {
  const z = SWITCH_SIZE[size] || SWITCH_SIZE.sm;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange && onChange(!checked)}
      // public/index.html gives every <button> a 32px minimum (44px on touch
      // screens), which turned the 20px track into a circle with the knob
      // hanging off its edge (Sahil, 2026-10-13). That rule is unlayered, so
      // it beats any Tailwind utility; it now excludes [role="switch"].
      className={`relative shrink-0 rounded-full border transition-colors p-0 ${z.track} ` +
        `${checked ? (SWITCH_TONE[tone] || SWITCH_TONE.brand) : "bg-neutral-200 border-neutral-300"} ` +
        `disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING} ${className}`}
    >
      <span className={`absolute top-[2px] bg-white rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-all ${z.knob} ${checked ? z.on : z.off}`} />
    </button>
  );
}

// ---- MENU ITEM ----
// A row in a dropdown menu.
//
// Eighteen of these were hand-written across the user menu, the bank-feed
// menus, the company menu and the tenant menus, and they drifted the
// usual way: px-3 py-2 vs px-4 py-2.5, gap-2 vs gap-3, and each site
// spelling its own hover colour to match the item's tone (a Disconnect
// item wanting danger, a Reactivate item wanting positive).
//
// `tone` names that instead, so the text and hover always agree -- they
// did not everywhere: one danger item had a neutral hover, so the row
// turned grey under a red label.
// NOTE for the next migration of this kind: the app spells its danger
// text as BOTH text-danger-500 and text-danger-600. A tone-detection pass
// that matched only -600 read the Logout row as neutral and silently
// dropped its red, which e2e/57 caught by comparing the computed colour
// of the destructive row against a neutral one.
const MENU_TONE = {
  neutral:  "text-neutral-700 hover:bg-brand-50",
  danger:   "text-danger-600 hover:bg-danger-50",
  positive: "text-positive-600 hover:bg-positive-50",
  brand:    "text-brand-700 hover:bg-brand-50",
};
// A floating panel that opens off a trigger and is drawn at the document
// root, so no card's overflow:hidden can clip it (the tenant page's "New…"
// lease menu showed 2 of its 5 actions; the Utilities filter panel opened
// off the left edge of a phone). It stays inside the viewport, closes on
// an outside tap or Escape, and on a phone takes the full width.
//   <Popover open={open} onClose={...} anchorRef={btnRef} align="right" width={240}>…</Popover>
export function Popover({ open, onClose, anchorRef, align = "right", width = 240, className = "", children, role = "menu" }) {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  const place = useCallback(() => {
    const a = anchorRef?.current; if (!a) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 8;
    const phone = vw < 480;
    const w = phone ? vw - 2 * gap : Math.min(width, vw - 2 * gap);
    let left = phone ? gap : (align === "right" ? r.right - w : r.left);
    left = Math.max(gap, Math.min(left, vw - w - gap));
    const h = panelRef.current ? panelRef.current.scrollHeight : 0;
    const below = r.bottom + 4, spaceBelow = vh - below - gap;
    // Below the trigger when it fits; else above it when that fits; else
    // moved up over the trigger so the whole panel is on screen (it has a
    // backdrop, so covering the button is fine).
    let top = below;
    if (h && h > spaceBelow) top = r.top - 4 - h > gap ? r.top - 4 - h : Math.max(gap, vh - h - gap);
    setPos({ left, top, width: w, maxHeight: Math.max(120, vh - top - gap) });
  }, [anchorRef, align, width]);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    const key = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", key);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); document.removeEventListener("keydown", key); };
  }, [open, place, onClose]);
  if (!open) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 z-[1090]" onClick={() => onClose?.()} aria-hidden="true" />
      <div ref={panelRef} role={role} style={pos ? { position: "fixed", left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight, visibility: "visible" } : { position: "fixed", left: -9999, top: 0, visibility: "hidden" }}
        className={`z-[1100] overflow-y-auto bg-white border border-neutral-200 rounded-xl shadow-pop ${className}`}>
        {children}
      </div>
    </>,
    document.body);
}

export function MenuItem({ icon, tone = "neutral", onClick, disabled, children, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left transition-colors ` +
        `${MENU_TONE[tone] || MENU_TONE.neutral} disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS_RING} ${className}`}
    >
      {icon && <span className="material-icons-outlined text-base">{icon}</span>}
      {children}
    </button>
  );
}

// ---- REMEMBERED LIST PREFERENCE ----
// The array counterpart of usePersistedView, for multi-select filters.
// Stored as JSON; an unparseable or non-array value is discarded rather
// than crashing the page it is filtering.
export function usePersistedList(key, initial = [], allowed = null) {
  const storageKey = "pm-list:" + key;
  const read = () => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return allowed ? parsed.filter(v => allowed.includes(v)) : parsed;
      }
    } catch { /* private window, blocked storage, or corrupt value */ }
    return initial;
  };
  const [state, setState] = useState(() => ({ key: storageKey, list: read() }));
  // A key that names the company (see Tenants) changes when the company
  // does: load THAT company's saved list instead of carrying the last one
  // over (audit FE-12).
  const list = state.key === storageKey ? state.list : read();
  if (state.key !== storageKey) setState({ key: storageKey, list });
  const setList = useCallback((v) => setState(s => ({ key: s.key, list: typeof v === "function" ? v(s.list) : v })), []);
  useEffect(() => {
    try { window.localStorage.setItem(state.key, JSON.stringify(state.list)); } catch { /* ignore */ }
  }, [state]);
  return [list, setList];
}

// ---- MULTI-SELECT FILTER ----
// A filter that can hold SEVERAL values, not one of many. A single-select
// filter forces a question the data does not ask: "current or past?" when
// the answer is "both, but not archived". Every filter on the tenants page
// was single-select, so narrowing to two statuses was impossible.
//
// An empty selection means ALL -- the same thing the old "All Status"
// option meant -- so the control has no separate "everything" entry to get
// out of step with the checkboxes.
//
// A <select multiple> would be the cheap version, but it is unusable in
// practice: no labels, ctrl-click to add, and a stray plain click wipes
// the selection. Checkboxes in a popover say what is on.
export function MultiSelect({
  allLabel = "All",
  options = [],            // [{ value, label, hint? }]
  value = [],              // selected values; [] means all
  onChange,
  className = "",
  ariaLabel,
  // A filter box inside the menu. Multi-select over a long list is no better
  // than a native <select> over the same list -- the Utilities property
  // filter has 112 addresses, and scrolling to tick three of them is the
  // problem, not the fix. On by default past eight options.
  searchable = null,
  searchPlaceholder = "Filter…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const MENU_W = 248;

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    // position:fixed, so it cannot follow a scroll -- close instead
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const sel = new Set(value);
  const labelFor = v => (options.find(o => o.value === v) || {}).label || v;
  // Name the first choice and count the rest: "Current +2" tells you what
  // is on without a tooltip, where "3 selected" does not.
  const summary = !value.length ? allLabel
    : value.length === 1 ? labelFor(value[0])
    : `${labelFor(value[0])} +${value.length - 1}`;

  const toggle = (v) => {
    const next = sel.has(v) ? value.filter(x => x !== v) : [...value, v];
    onChange(next);
  };

  const showSearch = searchable === null ? options.length > 8 : !!searchable;
  const q = query.trim().toLowerCase();
  // A ticked option stays listed even when it does not match the query, so
  // the menu can never hide a selection you are about to lose track of.
  const shown = q
    ? options.filter(o => o.label.toLowerCase().includes(q) || sel.has(o.value))
    : options;

  const openMenu = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const estH = 52 + Math.min(options.length, 10) * 34;
      const below = window.innerHeight - r.bottom;
      setPos({
        top: below < estH ? Math.max(8, r.top - estH - 4) : r.bottom + 4,
        left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_W - 8)),
      });
    }
    setOpen(true);
  };

  return (
    <span className={"relative inline-flex " + className}>
      <button
        ref={btnRef}
        type="button"
        onClick={openMenu}
        aria-label={ariaLabel || allLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={"inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm bg-white text-left " +
          (value.length ? "border-brand-400 text-brand-700 font-medium" : "border-neutral-200 text-neutral-600") +
          " hover:border-brand-300 " + FOCUS_RING}
      >
        <span className="truncate max-w-48">{summary}</span>
        {value.length > 0 && (
          <span className="text-2xs bg-brand-100 text-brand-700 rounded-full px-1.5 py-0.5 font-semibold">{value.length}</span>
        )}
        <span className="material-icons-outlined text-base leading-none text-neutral-400">expand_more</span>
      </button>
      {open && <>
        <div className="fixed inset-0 z-30" onClick={e => { e.stopPropagation(); setOpen(false); }} />
        <div
          role="listbox"
          aria-multiselectable="true"
          onClick={e => e.stopPropagation()}
          className="fixed z-40 bg-white border border-neutral-200 rounded-xl shadow-pop py-1 max-h-80 overflow-y-auto"
          style={{ top: pos.top, left: pos.left, width: MENU_W }}
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className={"w-full text-left px-3 py-2 text-sm border-b border-neutral-100 " +
              (value.length ? "text-brand-600 hover:bg-brand-50" : "text-neutral-400 cursor-default")}
            disabled={!value.length}
          >
            {allLabel}{value.length ? " — clear selection" : " (showing everything)"}
          </button>
          {showSearch && (
            <div className="px-2 py-1.5 border-b border-neutral-100 sticky top-0 bg-white">
              <input
                type="text" value={query} autoFocus
                onChange={e => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="w-full border border-neutral-200 rounded-lg px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
            </div>
          )}
          {shown.length === 0 && (
            <div className="px-3 py-3 text-xs text-neutral-400 text-center">Nothing matches "{query}"</div>
          )}
          {shown.map(o => (
            <label key={o.value}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 hover:bg-brand-50/60 cursor-pointer">
              <input type="checkbox" checked={sel.has(o.value)} onChange={() => toggle(o.value)}
                className="rounded accent-brand-600" />
              <span className="truncate" title={o.label}>{o.label}</span>
              {o.hint && <span className="ml-auto text-2xs text-neutral-400 shrink-0">{o.hint}</span>}
            </label>
          ))}
        </div>
      </>}
    </span>
  );
}

// ---- REMEMBERED VIEW PREFERENCE ----
// Cards / Table / Compact was reset to Cards on every mount, so choosing
// Table meant choosing it again after each visit to the page. The choice
// is a preference, not page state.
//
// Per page rather than one global setting, because the toggles do not
// offer the same modes -- Utilities has no Compact -- and a shared value
// would silently fall back whenever a page could not honour it. `allowed`
// guards against a stored value the page no longer supports.
export function usePersistedView(key, initial, allowed) {
  const storageKey = "pm-view:" + key;
  const [view, setView] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved && (!allowed || allowed.includes(saved))) return saved;
    } catch { /* private window, blocked storage */ }
    return initial;
  });
  useEffect(() => {
    try { window.localStorage.setItem(storageKey, view); } catch { /* ignore */ }
  }, [storageKey, view]);
  return [view, setView];
}

// ---- SEARCH TRIGGER ----
// The visible entry point to the command palette.
//
// The palette already existed and was good, but it was reachable ONLY by
// Cmd/Ctrl-K -- nothing on screen said so, so for anyone who had not been
// told, the app had no search. QuickBooks puts a search box in the top bar
// and that is where people look for it.
//
// It is a BUTTON that looks like a text field, not a real input. The
// palette owns the query, the filtering and the keyboard handling; a
// second input here would either duplicate all of that or have to forward
// every keystroke into it. Looking like a field is the affordance; being
// one would be a second implementation.
export function SearchTrigger({ onOpen, hint = "K", placeholder = "Search or jump to…", className = "" }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${placeholder} (keyboard shortcut ${hint})`}
      className={"group flex items-center gap-2 w-full min-w-0 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-left " +
        "hover:border-brand-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 " +
        "focus-visible:ring-offset-1 transition-colors " + className}
    >
      <span className="material-icons-outlined text-base text-neutral-400 group-hover:text-brand-500">search</span>
      <span className="flex-1 min-w-0 truncate text-xs text-neutral-400">{placeholder}</span>
      <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-white px-1.5 py-0.5 text-2xs font-semibold text-neutral-400">
        {hint}
      </kbd>
    </button>
  );
}

// True on a phone-width screen (under Tailwind's md breakpoint, 767px);
// tracks the viewport. Used wherever a desktop layout (two panes, a wide
// table) gets a stacked phone layout instead.
export function useIsPhone() {
  const [phone, setPhone] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  return phone;
}

// True below a tablet-landscape width (1100px): where a list table no longer
// fits beside the sidebar. An iPad (810px portrait, 1080px landscape) is
// over the phone breakpoint but the Loans and Journal Entries tables are
// 1,281-1,304px wide, so they overflowed there exactly as on a phone.
export function useIsCompact(max = 1100) {
  const q = `(max-width: ${max}px)`;
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(q).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    on();
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, [q]);
  return m;
}

// ---- COMPANY SCOPE ----
// The active company id, provided once at the app root.
//
// Shareable/new-tab links have to name their company, because the app
// strips ?company= from the address bar immediately after selecting one --
// so by the time a user cmd-clicks a figure there is nothing left in the
// URL to copy forward. The alternative was threading companyId through all
// fifteen LedgerLink call sites, where forgetting one produces a link that
// works for a single-company user and dumps a multi-company user at the
// company selector. A context cannot be forgotten at a call site.
export const CompanyScope = React.createContext(null);
export function useCompanyScope() { return React.useContext(CompanyScope); }

// ---- DRILL LINK ----
// A figure or label that drills through to its detail. QuickBooks renders
// these as ordinary text and only reveals the link on hover, which is what
// keeps a report reading as a statement rather than as a page of hyperlinks.
//
// It went the other way first -- brand colour plus a permanent dotted
// underline -- because nothing had marked the totals as clickable. That
// over-corrected: every account name and every amount in the Balance Sheet
// came out blue and underlined. Sahil: "unneccesary underlines and colours
// coding in the reports". Discoverability now rides on the cursor and the
// hover, as it does in QuickBooks.
//
// Every drillable figure in the app resolves its affordance from here, so
// this constant is the only place the decision lives.
export const DRILL_LINK =
  "text-inherit no-underline hover:text-brand-700 hover:underline hover:decoration-solid underline-offset-2 cursor-pointer rounded " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1";

// ---- TEXT LINK ----
// Underline-on-hover button that mirrors the common `text-xs text-COLOR-600
// hover:underline` pattern used for inline row actions (Edit / Delete /
// Report). Not a routed <a> — purely a click handler with link visuals.
export function TextLink({ tone = "brand", size = "xs", underline = true, className = "", children, ...props }) {
  const tones = {
    brand:    "text-brand-600 hover:text-brand-700",
    danger:   "text-danger-500 hover:text-danger-700",
    neutral:  "text-neutral-500 hover:text-neutral-700",
    success:  "text-success-600 hover:text-success-700",
    positive: "text-positive-600 hover:text-positive-700",
    warn:     "text-warn-600 hover:text-warn-700",
    notice:   "text-notice-500 hover:text-notice-700",
    info:     "text-info-600 hover:text-info-700",
    highlight:"text-highlight-600 hover:text-highlight-700",
    accent:   "text-accent-600 hover:text-accent-800",
    subtle:   "text-subtle-500 hover:text-subtle-700",
  };
  const sizes = { xs: "text-xs", sm: "text-sm", md: "text-base", lg: "text-lg", xl: "text-xl" };
  return (
    <button className={`${tones[tone] || tones.brand} ${sizes[size] || sizes.xs}${underline ? " hover:underline" : ""} ${FOCUS_RING} rounded ${className}`} {...props}>
      {children}
    </button>
  );
}

// ---- CHIP ----
// Small rounded pill for interactive, non-status labels — e.g. an "Edit"
// or "Filter by X" affordance. Separate from <Badge> (which is for static
// status indicators like "Paid" / "Overdue").
export function Chip({ tone = "neutral", className = "", children, ...props }) {
  const tones = {
    neutral: "bg-neutral-100 text-neutral-600 hover:bg-neutral-200",
    brand:   "bg-brand-50 text-brand-700 hover:bg-brand-100",
    success: "bg-success-50 text-success-700 hover:bg-success-100",
    danger:  "bg-danger-50 text-danger-700 hover:bg-danger-100",
  };
  return (
    <button className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition-colors ${tones[tone] || tones.neutral} ${className}`} {...props}>
      {children}
    </button>
  );
}

// Mouse affordance for a whole card or row. Returns ONLY onClick --
// deliberately no role and no tabIndex.
//
// The first version of this added role="button" + tabIndex to the card
// container. That is invalid when the card contains its own buttons,
// links or checkboxes: a widget role may not contain other widgets, and
// screen readers stop exposing the inner controls. axe caught it as
// nested-interactive across 73 tenant cards. The keyboard path belongs
// on ONE real control inside the card -- see CardOpenButton.
export function clickable(onActivate) {
  return { onClick: onActivate };
}

// The real, focusable control inside a clickable card or row. Render the
// record's primary label through this so keyboard and screen-reader
// users get exactly one correctly-named way in, without the container
// swallowing everything else.
export function CardOpenButton({ onActivate, label, title, className = "", children }) {
  return (
    <button
      type="button"
      aria-label={label}
      // A name narrowed to fit its column is truncated by CSS, so the full
      // value has to stay reachable on hover.
      title={title}
      onClick={(e) => { e.stopPropagation(); onActivate(e); }}
      className={"text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 " + className}
    >{children}</button>
  );
}

// For LEAF clickable elements only -- a drop zone, an "add" tile, a
// badge -- i.e. ones with no interactive descendants. Do NOT put this on
// a card or row that contains buttons or links; role="button" there is
// nested-interactive and hides the inner controls from assistive tech.
// Spread alongside the existing onClick; it forwards Enter/Space.
//   <div {...keyboardActivate} onClick={() => ...}>
export const keyboardActivate = {
  role: "button",
  tabIndex: 0,
  onKeyDown: (e) => {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    if (e.target !== e.currentTarget) return; // inner controls keep their own keys
    e.preventDefault();
    e.currentTarget.click();
  },
};

// ============ REPORT DENSITY ============
// Reports open compact, like QuickBooks; Comfortable is the old spacing.
// One setting for every report and ledger, remembered per browser, and kept
// in step between the components showing at once.
const DENSITY_KEY = "report_density";
const DENSITY_EVENT = "report-density-change";
export function useReportDensity() {
  const read = () => { try { return localStorage.getItem(DENSITY_KEY) === "comfortable" ? "comfortable" : "compact"; } catch (_) { return "compact"; } };
  const [density, setDensityState] = useState(read);
  useEffect(() => {
    const on = () => setDensityState(read());
    window.addEventListener(DENSITY_EVENT, on);
    return () => window.removeEventListener(DENSITY_EVENT, on);
  }, []);
  const setDensity = useCallback(d => {
    try { localStorage.setItem(DENSITY_KEY, d); } catch (_) { /* private window: this page only */ }
    setDensityState(d);
    window.dispatchEvent(new Event(DENSITY_EVENT));
  }, []);
  return [density, setDensity];
}
export function DensityToggle({ density, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-neutral-200 bg-white p-0.5 text-xs" role="group" aria-label="Report density">
      {[["compact", "Compact"], ["comfortable", "Comfortable"]].map(([k, l]) => (
        <button key={k} type="button" aria-pressed={density === k} onClick={() => onChange(k)}
          className={"px-2.5 py-1 rounded-md " + (density === k ? "bg-neutral-800 text-white" : "text-neutral-600 hover:bg-neutral-100")}>{l}</button>
      ))}
    </div>
  );
}

// ============ DATA TABLE ============
// One table, so 62 of them stop each inventing their own.
//
// The audit found 62 hand-rolled <table>s across 15 files, with 9
// different header treatments and 24 distinct cell paddings. That
// inconsistency is what reads as unpolished -- two tables side by side in
// the same product disagreeing about what a column heading looks like.
//
// DENSITY LIVES HERE AND NOWHERE ELSE. Every padding below is a constant
// in this file, so changing row height is one edit rather than 24.
const TD = {
  normal:  "px-4 py-2.5",
  compact: "px-3 py-1.5",
  // Reports in compact mode: QuickBooks-like, one ~24px line per row.
  tight:   "px-2 py-0.5",
};
const TH = {
  normal:  "px-4 py-2.5",
  compact: "px-3 py-1.5",
  tight:   "px-2 py-1.5",
};
// The table's text size per density (tight reads like a QuickBooks report).
const TABLE_TEXT = { normal: "text-sm", compact: "text-sm", tight: "text-xs" };
const ALIGN = { left: "text-left", right: "text-right", center: "text-center" };

// A column is { key, label, align, width, render, className, thClassName }.
// `render(row, index)` overrides the default cell, which is row[key].
// `align: "right"` also applies tabular figures, since a right-aligned
// column is nearly always numeric and digits must line up.
export function DataTable({
  columns = [],
  rows = [],
  rowKey,
  onRowClick,
  groups = null,        // [{ key, label, rows, footer, count, collapsed, onToggle }] -- one tbody each;
                        // with onToggle the heading collapses its rows (footer stays)
  footer = null,        // [{ label, cells:[], strong }] -- total rows
  empty = "Nothing to show",
  loading = false,
  density = "normal",
  stickyHeader = false,
  // Some tables are deliberately header-less -- a journal entry's lines
  // inside a detail panel, a mini transaction list. Rendering a header
  // there would ADD a row that was never in the original.
  hideHeader = false,
  // Master-detail: an extra full-width row under a row, for the expanded
  // panel pattern -- click a bank transaction and its matching and
  // posting controls open beneath it. Return null for rows that are not
  // expanded. Without this in the primitive, every such table stays
  // hand-rolled, and flat-migrating one silently deleted 257 lines of
  // panel markup.
  expandedRow = null,
  // A first column pinned while the rest scrolls, for reports with one
  // column per property.
  stickyFirstColumn = false,
  // The mirror of stickyFirstColumn, for a crosstab whose TOTAL column
  // must stay on screen. The P&L by Property matrix has one column per
  // property; with forty of them an unpinned total is off-screen, which
  // reads as "this report has no totals".
  stickyLastColumn = false,
  // Scale a wide table down to fit a narrow screen; see FitToWidth below.
  fitToWidth = false,
  // Per-row DOM attributes, as rowAttrs(row, index) => object. Keyboard
  // navigation needs to find a row by index from document.activeElement,
  // which needs a real attribute on the <tr>; the JE line editor carried
  // data-je-line={i} for exactly that and lost it when it was flat-migrated
  // here, silently breaking every shortcut that walks lines.
  rowAttrs = null,
  // Per-row classes, as rowClassName(row, index). A row can be marked in
  // ways a column cannot express: Banking's keyboard cursor draws a
  // ring-2 ring-inset on the row it is on, and the expanded row gets a
  // tinted background. Both lived on the hand-rolled <tr> and were lost
  // when it was migrated -- the cursor became invisible.
  rowClassName = null,
  // Sorting lives in the primitive, not in a per-page SortTh. Pass
  // sort={{ key, dir }} plus onSort(key), and give a column `sort: true`
  // (it sorts by its own key) or `sort: "other_key"`.
  //
  // Tenants.js had its own SortTh, which is how its whole table got
  // destroyed: the migration tool scans for literal <th>, saw one (the
  // checkbox header) behind seven <SortTh> components, and emitted a
  // one-column table. Every page that grows a sortable header from here
  // on gets it from the same place, so there is nothing bespoke left to
  // misread. It also puts aria-sort on the <th>, where it belongs --
  // SortTh had it on the inner <button>, where assistive tech does not
  // look for it.
  sort = null,
  onSort = null,
  // Drag-to-resize column widths. Requires every column to carry a `width`
  // (a px number): with resizing on, the table switches to fixed layout so a
  // drag moves the column the user grabbed instead of the browser
  // redistributing every column at once. Without a starting width for each,
  // fixed layout would divide the table equally and the first render would
  // look wrong.
  //
  // Widths are remembered per table in localStorage under storageKey, so a
  // column someone widened stays widened. Storage is per-viewer and
  // best-effort: every read and write is guarded, because a private window
  // or blocked site data throws rather than returning null.
  resizable = false,
  storageKey = null,
  scroll = true,
  className = "",
  ariaLabel,
  // On a phone a plain list renders as stacked cards (the first column
  // as the card's title, the rest as label: value lines, an unlabelled
  // column -- actions, a checkbox -- as a row of its own at the foot).
  // The mobile audit found every table page laid out at desktop width
  // inside its card: Loans 1,072px and Journal Entries 1,049px on a
  // 390px screen, with amounts, status and ACTIONS off-screen and only
  // a thin scrollbar to say so. phone="table" keeps the table (a report
  // crosstab that fits by scaling, a line editor with its own phone
  // layout). Grouped, expandable and scaled tables keep the table too.
  phone = "cards",
  // Draw the first `pageSize` rows with "Show more" / "Show all" below,
  // instead of every row at once: the Journal Entries list rendered
  // thousands of rows on open (audit OPS-15). Flat lists only; totals in
  // `footer` still cover every row the caller passed.
  pageSize = null,
}) {
  const [shown, setShown] = useState(pageSize || Infinity);
  // a new list (filter, search, other company) starts at the first page
  useEffect(() => { setShown(pageSize || Infinity); }, [pageSize, rows.length]);
  const paged = !!pageSize && !groups && rows.length > shown;
  const visible = paged ? rows.slice(0, shown) : rows;
  const moreBar = paged ? (
    <div className="flex items-center justify-center gap-3 py-3 text-sm text-neutral-500" data-table-more="1">
      <span>Showing {visible.length.toLocaleString()} of {rows.length.toLocaleString()}</span>
      <button type="button" className="font-medium text-brand-600 hover:underline" onClick={() => setShown(n => n + pageSize)}>Show {Math.min(pageSize, rows.length - visible.length).toLocaleString()} more</button>
      <button type="button" className="font-medium text-brand-600 hover:underline" onClick={() => setShown(Infinity)}>Show all</button>
    </div>
  ) : null;
  const td = TD[density] || TD.normal;
  const th = TH[density] || TH.normal;
  const isPhone = useIsCompact();   // phones AND tablets: see useIsCompact

  const [colWidths, setColWidths] = useState(() => {
    if (!resizable || !storageKey) return {};
    try { return JSON.parse(window.localStorage.getItem("dt-widths:" + storageKey) || "{}") || {}; }
    catch { return {}; }
  });
  useEffect(() => {
    if (!resizable || !storageKey) return;
    try { window.localStorage.setItem("dt-widths:" + storageKey, JSON.stringify(colWidths)); } catch { /* private window, blocked storage */ }
  }, [colWidths, resizable, storageKey]);

  // Pointer events, not mouse: this has to keep tracking when the cursor
  // leaves the header (which it does immediately on a downward drag), and
  // setPointerCapture is what guarantees the move/up pair arrive.
  const startResize = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    const cell = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startW = cell ? cell.getBoundingClientRect().width : 120;
    const onMove = (ev) => {
      // 56px floor: narrower than this and the header label is unreadable,
      // so a column cannot be dragged into being unidentifiable.
      setColWidths(w => ({ ...w, [key]: Math.max(56, Math.round(startW + (ev.clientX - startX))) }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
    };
    // Without this the drag selects the table text as it goes.
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const widthOf = (c) => colWidths[c.key] ?? c.width;
  // `sort: true` means "sort by my own key"; a string names another field.
  const sortKeyOf = c => (c.sort === true ? c.key : (typeof c.sort === "string" ? c.sort : null));
  const cols = columns.length || 1;
  const keyOf = (r, i) => (rowKey ? rowKey(r, i) : (r && r.id != null ? r.id : i));

  const cell = (col, row, i) => {
    const v = col.render ? col.render(row, i) : (row ? row[col.key] : null);
    return (
      <td
        key={col.key}
        className={[
          td,
          stickyFirstColumn && col === columns[0] ? "sticky left-0 z-10 bg-white" : "",
          stickyLastColumn && col === columns[columns.length - 1] ? "sticky right-0 z-10 bg-white" : "",
          ALIGN[col.align] || ALIGN.left,
          col.align === "right" ? "tnum" : "",
          // A className may be a FUNCTION of the row. Several tables colour
          // a cell by its own value -- an overdue balance in red, a
          // negative figure in danger -- and forcing those to a static
          // string would silently drop the conditional styling.
          (typeof col.className === "function" ? col.className(row, i) : col.className) || "",
        ].filter(Boolean).join(" ")}
        style={col.width ? { width: col.width } : undefined}
      >
        {v}
      </td>
    );
  };

  const bodyRows = (list) =>
    list.flatMap((row, i) => {
      const expanded = expandedRow ? expandedRow(row, i) : null;
      const main = (
        <tr
          key={keyOf(row, i)}
          {...(rowAttrs ? rowAttrs(row, i) : null)}
          onClick={onRowClick ? () => onRowClick(row, i) : undefined}
          className={[
            "border-t border-neutral-100",
            onRowClick ? "cursor-pointer hover:bg-brand-50/40 transition-colors" : "",
            (rowClassName ? rowClassName(row, i) : "") || "",
          ].filter(Boolean).join(" ")}
        >
          {columns.map(c => cell(c, row, i))}
        </tr>
      );
      // The detail row spans every column, computed here rather than by
      // the caller -- a hand-written colSpan is what put a 9-column
      // header over 8-column rows in the ledger.
      return expanded
        ? [main, <tr key={String(keyOf(row, i)) + "-detail"}><td colSpan={cols} className="p-0">{expanded}</td></tr>]
        : [main];
    });

  // A footer row gives cells for the LAST n columns and spans the rest, so
  // a total lines up under its column without every caller counting
  // colSpans by hand -- the mistake that put a 9-column header over
  // 8-column rows in the ledger.
  const footerRow = (f, i) => {
    const given = (f.cells || []).length;
    const span = Math.max(cols - given, 1);
    return (
      // f.className lets a caller keep an emphasis the primitive does not
      // have a name for -- the P&L matrix's bottom line was font-black over
      // a border-neutral-800 rule, and flattening every total row to one
      // weight would have lost the distinction between a section subtotal
      // and the report's answer.
      <tr key={(typeof f.label === "string" ? f.label : null) || i}
          className={(f.className || "border-t-2 border-neutral-300 " + (f.strong ? "font-bold" : "font-semibold"))}>
        <td className={[td, ALIGN[columns[0] && columns[0].align] || ALIGN.left,
          stickyFirstColumn ? "sticky left-0 z-10 bg-neutral-100" : ""].filter(Boolean).join(" ")} colSpan={span}>{f.label}</td>
        {(f.cells || []).map((c, ci) => {
          const col = columns[cols - given + ci] || {};
          return (
            <td key={ci} className={[td, ALIGN[col.align] || ALIGN.right, "tnum",
              // Without this a pinned column lost its pin on every total
              // row -- the totals scrolled away while the data stayed put.
              stickyLastColumn && col === columns[columns.length - 1] ? "sticky right-0 z-10 bg-neutral-100" : "",
            ].filter(Boolean).join(" ")}>
              {c}
            </td>
          );
        })}
      </tr>
    );
  };

  const table = (
    <table className={"w-full " + (TABLE_TEXT[density] || "text-sm") + " border-collapse " + (resizable ? "table-fixed " : "") + className} aria-label={ariaLabel}>
      {resizable && (
        <colgroup>
          {columns.map(c => <col key={c.key} style={{ width: widthOf(c) ? widthOf(c) + "px" : undefined }} />)}
        </colgroup>
      )}
      {!hideHeader && (
      <thead className={"bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wide " + (stickyHeader ? "sticky top-0 z-10" : "")}>
        <tr>
          {columns.map(c => (
            <th
              key={c.key}
              scope="col"
              className={[th, ALIGN[c.align] || ALIGN.left, "font-semibold",
                resizable ? "relative" : "",
                stickyFirstColumn && c === columns[0] ? "sticky left-0 z-20 bg-neutral-50" : "",
                stickyLastColumn && c === columns[columns.length - 1] ? "sticky right-0 z-20 bg-neutral-50" : "",
                c.thClassName || ""].filter(Boolean).join(" ")}
              style={widthOf(c) ? { width: widthOf(c) } : undefined}
              aria-sort={sortKeyOf(c) && sort && sort.key === sortKeyOf(c)
                ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
            >
              {sortKeyOf(c) && onSort ? (
                <button
                  type="button"
                  onClick={() => onSort(sortKeyOf(c))}
                  className="inline-flex items-center gap-1 uppercase hover:text-neutral-700"
                  aria-label={`Sort by ${c.label}`}
                >
                  {c.label}
                  {sort && sort.key === sortKeyOf(c) && (
                    <span className="material-icons-outlined text-sm leading-none">
                      {sort.dir === "asc" ? "arrow_upward" : "arrow_downward"}
                    </span>
                  )}
                </button>
              ) : c.label}
              {/* Grab handle. Double-click restores this column to the
                  width the caller specified, so a drag is never a one-way
                  door. The last column has none: there is nothing to its
                  right to give or take space from. */}
              {resizable && c !== columns[columns.length - 1] && (
                <span
                  onPointerDown={(e) => startResize(c.key, e)}
                  onDoubleClick={() => setColWidths(w => { const n = { ...w }; delete n[c.key]; return n; })}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`Resize ${typeof c.label === "string" ? c.label : c.key} column`}
                  title="Drag to resize — double-click to reset"
                  // z-20: the handle is translated half outside its own
                  // <th>, and a later sibling cell paints over a static
                  // element, so without a stacking order the right half of
                  // every grab area was dead -- elementFromPoint at the
                  // handle's centre returned the NEXT header, not the
                  // handle. It looked draggable and was not.
                  className="absolute top-0 right-0 z-20 h-full w-2 translate-x-1/2 cursor-col-resize select-none hover:bg-brand-300/70 active:bg-brand-400"
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
      )}

      {loading ? (
        // Skeleton rows, not a single "Loading…" line. The old version
        // collapsed the whole row area to one line, so the page jumped
        // when the data landed. Five rows is enough to read as a table
        // without pretending to know how many rows are coming.
        <tbody aria-busy="true">
          {[0, 1, 2, 3, 4].map(r => (
            <tr key={r} className="border-t border-neutral-100">
              {columns.map((c, ci) => (
                <td key={c.key || ci} className={[td, ALIGN[c.align] || ALIGN.left].join(" ")}>
                  <Skeleton className={`h-3 ${c.align === "right" ? "w-12" : ci === 0 ? "w-32" : "w-20"}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ) : groups ? (
        <>
          {groups.map(g => (
            <tbody key={g.key}>
              {g.label && (
                <tr className={"bg-neutral-100/80" + (g.onToggle ? " cursor-pointer hover:bg-neutral-200/60" : "")}
                    onClick={g.onToggle || undefined} aria-expanded={g.onToggle ? !g.collapsed : undefined}>
                  <td colSpan={cols} className={td + " text-xs font-bold text-neutral-700 border-t-2 border-neutral-300" + (stickyFirstColumn ? " sticky left-0 z-10 bg-neutral-100" : "")}>
                    {g.onToggle && <span className="material-icons-outlined text-sm leading-none align-middle mr-1 text-neutral-500">{g.collapsed ? "chevron_right" : "expand_more"}</span>}
                    {g.label}{g.count != null && <span className="font-normal text-neutral-500"> ({g.count})</span>}
                  </td>
                </tr>
              )}
              {!g.collapsed && bodyRows(g.rows || [])}
              {/* A section can end with more than one total: the P&L
                  matrix follows "Total COGS" with "Gross Profit", and
                  "Total for Expenses" with "Net Operating Income". */}
              {g.footer && (Array.isArray(g.footer) ? g.footer : [g.footer]).map(footerRow)}
            </tbody>
          ))}
          {footer && <tbody>{footer.map(footerRow)}</tbody>}
          {groups.every(g => !(g.rows || []).length) && (
            <tbody><tr><td colSpan={cols} className={td + " text-center text-neutral-400 py-8"}>{empty}</td></tr></tbody>
          )}
        </>
      ) : (
        <tbody>
          {rows.length ? bodyRows(visible) : (
            <tr><td colSpan={cols} className={td + " text-center text-neutral-400 py-8"}>{empty}</td></tr>
          )}
          {moreBar && <tr><td colSpan={cols} className="p-0">{moreBar}</td></tr>}
          {footer && rows.length > 0 && footer.map(footerRow)}
        </tbody>
      )}
    </table>
  );

  if (isPhone && phone === "cards" && !groups && !expandedRow && !fitToWidth && !stickyFirstColumn && !stickyLastColumn) {
    const titleCol = columns[0];
    const labelOf = (c) => (typeof c.label === "string" ? c.label.trim() : "");
    const card = (row, i) => {
      const title = titleCol ? (titleCol.render ? titleCol.render(row, i) : row[titleCol.key]) : null;
      const lines = [], foot = [];
      columns.slice(1).forEach(c => {
        const v = c.render ? c.render(row, i) : (row ? row[c.key] : null);
        if (v == null || v === "" || v === false) return;
        if (!labelOf(c)) foot.push(<div key={c.key} className="flex flex-wrap gap-2 items-center justify-end">{v}</div>);
        else lines.push(
          <div key={c.key} className="flex justify-between gap-3 text-sm min-w-0">
            <span className="text-2xs uppercase tracking-wide text-neutral-400 shrink-0 pt-0.5">{labelOf(c)}</span>
            <span className={"min-w-0 text-right break-words " + (c.align === "right" ? "tnum " : "") + ((typeof c.className === "function" ? c.className(row, i) : c.className) || "")}>{v}</span>
          </div>);
      });
      return (
        <div key={keyOf(row, i)} {...(rowAttrs ? rowAttrs(row, i) : null)}
          onClick={onRowClick ? () => onRowClick(row, i) : undefined}
          className={["px-3 py-3 space-y-1.5", onRowClick ? "cursor-pointer active:bg-brand-50/40" : "", (rowClassName ? rowClassName(row, i) : "") || ""].filter(Boolean).join(" ")}>
          {title != null && title !== "" && <div className={"text-sm font-semibold text-neutral-800 break-words " + ((typeof titleCol.className === "function" ? titleCol.className(row, i) : titleCol.className) || "")}>{title}</div>}
          {lines}
          {foot}
        </div>
      );
    };
    return (
      <div className={"divide-y divide-neutral-100 " + className} aria-label={ariaLabel} data-table-cards="1">
        {loading ? [0, 1, 2].map(r => <div key={r} className="px-3 py-3 space-y-2"><Skeleton className="h-3 w-40" /><Skeleton className="h-3 w-24" /></div>)
          : rows.length ? visible.map(card)
          : <div className="px-3 py-8 text-center text-sm text-neutral-400">{empty}</div>}
        {moreBar}
        {footer && rows.length > 0 && footer.map((f, i) => (
          <div key={i} className={"px-3 py-2 flex justify-between gap-3 text-sm bg-neutral-50 " + (f.strong ? "font-bold" : "font-semibold")}>
            <span>{f.label}</span>
            <span className="tnum text-right flex gap-3">{(f.cells || []).map((c, ci) => <span key={ci}>{c}</span>)}</span>
          </div>
        ))}
      </div>
    );
  }

  // Wide content scrolls inside its own container so the page body never
  // scrolls sideways.
  // fitToWidth: on a narrow screen, scale a wide table down so the WHOLE
  // thing is visible, then let the reader pinch in.
  //
  // A wide crosstab -- P&L by Property with a column per property -- sits
  // in its own overflow-x container on a phone, so pinch-zooming the page
  // does not help: the columns are clipped inside a box, and the figures
  // come out cut in half ("$26,10"). Sahil: "we should zoom out the
  // reports to a decent level and let the user zoom in with pinch if
  // needed."
  //
  // Scale rather than shrink the type, because scaling keeps the column
  // proportions and the tabular alignment intact; reducing font-size
  // reflows and re-wraps. transform-origin is top-left so the table stays
  // anchored, and the wrapper's height is scaled to match or the layout
  // keeps the unscaled height and leaves a gap beneath.
  if (!fitToWidth) {
    return scroll ? <div className="overflow-x-auto">{table}</div> : table;
  }
  return <FitToWidth>{table}</FitToWidth>;
}

// Measures its content against the space available and scales it down to
// fit, never up. Re-measures on resize and rotation.
function FitToWidth({ children, minScale = 0.45 }) {
  const boxRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  const [h, setH] = React.useState(null);

  React.useEffect(() => {
    const measure = () => {
      const box = boxRef.current, inner = innerRef.current;
      if (!box || !inner) return;
      const avail = box.clientWidth;
      // scrollWidth of the UNSCALED content: read it at scale 1.
      const natural = inner.scrollWidth;
      if (!avail || !natural) return;
      const next = natural <= avail ? 1 : Math.max(minScale, avail / natural);
      setScale(next);
      setH(next < 1 ? Math.ceil(inner.scrollHeight * next) : null);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && boxRef.current) ro.observe(boxRef.current);
    window.addEventListener("orientationchange", measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("orientationchange", measure); };
  }, [minScale, children]);

  return (
    <div ref={boxRef} className="overflow-x-auto" style={h ? { height: h } : undefined}>
      <div ref={innerRef}
        style={scale < 1 ? { transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` } : undefined}>
        {children}
      </div>
    </div>
  );
}

// ---- detail pages -------------------------------------------------------
// TenantPage and PropertyPage are the same shape: an identity header, a strip
// of reference panels, then cards. These four were written twice, once in
// each, which is the duplication this file exists to absorb.
export function DetailPanel({ title, children }) {
  return (
    <section className="p-3 border-r border-brand-50 last:border-r-0 max-md:border-r-0 max-md:border-b">
      <h3 className="text-2xs font-semibold tracking-[0.07em] uppercase text-neutral-400 mb-1.5">{title}</h3>
      {children}
    </section>
  );
}

export function DetailRow({ label, children, tone }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-0.5">
      <span className="text-neutral-400">{label}</span>
      <span className={tone === "warn" ? "text-warn-700 font-semibold" : tone === "positive" ? "text-positive-700 font-medium text-right" : "text-neutral-700"}>{children}</span>
    </div>
  );
}

export function DetailCard({ title, sub, action, children, flush }) {
  return (
    <div className="bg-white border border-brand-50 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-brand-50">
        <h2 className="text-sm font-semibold flex-1 text-neutral-700">{title}</h2>
        {sub && <span className="text-xs text-neutral-400">{sub}</span>}
        {action}
      </div>
      <div className={flush ? "" : "px-4 py-3.5"}>{children}</div>
    </div>
  );
}

// A record's own contradictions, stated where they are true rather than
// collected on a dashboard nobody opens.
export function DetailAlert({ children, fix, onFix }) {
  return (
    <div className="flex gap-2.5 items-start bg-warn-50 border border-warn-200 border-l-[3px] border-l-warn-500 rounded-r-xl px-3.5 py-2.5 mb-2.5 text-sm text-neutral-600">
      <span className="material-icons-outlined text-base text-warn-600">warning_amber</span>
      <div className="flex-1">{children}</div>
      {fix && <TextLink tone="brand" size="xs" onClick={onFix} className="whitespace-nowrap">{fix}</TextLink>}
    </div>
  );
}
